import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import { BrokerDetailsActions } from '@lockton-apps/brokers/action';
import {
  ClientActions,
  ClientListActions,
  ClientNewActions,
  ClientUpdateActions,
} from '@lockton-apps/clients/actions';
import {
  DashboardAdminActions,
  DashboardBrokerActions,
  DashboardBrokerLsosActions,
  DashboardBrokerNorwayActions,
} from '@lockton-apps/dashboard/action';
import {
  LsosRenewalsApplicationActions,
  LsosRenewalsOverviewActions,
} from '@lockton-apps/lsos-renewals/action';
import { QuoteDetailsActions } from '@lockton-apps/quotes/action';
import { ClientApiActions } from '@lockton-apps/shared/action/clients';
import { Client, ClientCreate } from '@lockton-apps/shared/interfaces';

import * as ClientsActions from './clients.actions';

export const SHARED_CLIENTS_FEATURE_KEY = 'clients';

export interface ClientsState extends EntityState<Client> {
  selectedId?: number | null; // which Clients record has been selected
  selectionIds?: number[];
  loaded: boolean; // has the Clients list been loaded
  contactAddComplete: boolean;
  subClientsAddComplete: boolean;
  addedClientId: number | null;
  createUpdate: {
    detailsComplete: boolean;
    currentStep: number;
  };
  addSubClientsFailed: {
    error: any;
    client: ClientCreate;
  }[];
  mutationsLoading: boolean;
  error?: unknown; // last known error (if any)
}

export interface ClientsPartialState {
  readonly [SHARED_CLIENTS_FEATURE_KEY]: ClientsState;
}

export const clientsAdapter: EntityAdapter<Client> =
  createEntityAdapter<Client>();

export const initialClientsState: ClientsState = clientsAdapter.getInitialState(
  {
    contactAddComplete: false,
    subClientsAddComplete: false,
    addedClientId: null,
    addSubClientsFailed: [],
    createUpdate: {
      detailsComplete: false,
      currentStep: 0,
    },
    filter: {},
    loaded: false,
    mutationsLoading: false,
  }
);

const reducer = createReducer(
  initialClientsState,

  // Load Clients
  on(
    ClientListActions.init,
    DashboardAdminActions.init,
    DashboardBrokerLsosActions.init,
    DashboardBrokerNorwayActions.init,
    DashboardBrokerActions.init,
    BrokerDetailsActions.init,
    ClientActions.init,
    QuoteDetailsActions.init,
    (state) => ({
      ...state,
      loaded: false,
      error: null,
    })
  ),
  on(
    ClientApiActions.loadClientsFailure,
    ClientApiActions.loadClientSubClientFailure,
    ClientsActions.loadClientSubClientsFailure,
    (state, { error }) => ({
      ...state,
      loaded: true,
      error,
    })
  ),

  /* Load */

  on(
    ClientApiActions.loadClientSuccess,
    ClientApiActions.clientAddedSuccess,
    (state, { client }) =>
      clientsAdapter.upsertOne(client, {
        ...state,
        loaded: true,
      })
  ),
  // Load And Select Client With Follow On Client Actions
  on(
    ClientApiActions.loadClientSuccessWithRelationshipChecks,
    (state, { client }) =>
      clientsAdapter.upsertOne(client, {
        ...state,
      })
  ),
  on(
    ClientApiActions.loadClientAndSubClientsSuccess,
    ClientsActions.loadClientSubClientsSuccess,
    ClientApiActions.loadClientSubClientSuccess,
    ClientApiActions.loadClientsSuccess,
    (state, { clients }) =>
      clientsAdapter.upsertMany(clients, {
        ...state,
        loaded: true,
      })
  ),

  on(
    ClientUpdateActions.clientUpdated,
    ClientNewActions.clientDataAdded,
    (state) => ({
      ...state,
      mutationsLoading: true,
      error: null,
    })
  ),

  on(ClientApiActions.clientUpdatedSuccess, (state, { client }) =>
    clientsAdapter.upsertOne(client, {
      ...state,
      mutationsLoading: false,
    })
  ),
  on(
    ClientApiActions.clientAddedFailure,
    ClientApiActions.clientUpdatedFailure,
    ClientApiActions.primaryClientAddedFailure,
    (state, { error }) => ({
      ...state,
      mutationsLoading: false,
      error,
    })
  ),

  on(ClientApiActions.primaryClientAddedSuccess, (state, { client }) =>
    clientsAdapter.upsertOne(client, {
      ...state,
    })
  ),

  on(ClientApiActions.secondaryClientAddedSuccess, (state, { addedClient }) =>
    clientsAdapter.upsertOne(addedClient, {
      ...state,
    })
  ),

  on(ClientApiActions.clientDataAddedComplete, (state) => ({
    ...state,
    mutationsLoading: false,
  })),

  on(
    LsosRenewalsOverviewActions.init,
    LsosRenewalsApplicationActions.loadClientAsBroker,
    (state) => ({
      ...state,
      loaded: false,
      error: null,
    })
  ),
  on(
    LsosRenewalsApplicationActions.loadClientAsBrokerSuccess,
    (state, { client }) =>
      clientsAdapter.setOne(client, {
        ...state,
        loaded: true,
      })
  ),
  on(
    LsosRenewalsApplicationActions.loadClientAsBrokerFailure,
    (state, { error }) => ({
      ...state,
      loaded: true,
      error,
    })
  ),

  on(ClientApiActions.primaryClientAddedSuccess, (state, { client }) =>
    clientsAdapter.addOne(client, {
      ...state,
    })
  ),
  on(ClientApiActions.primaryClientAddedFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // Load Client Sub Clients
  on(ClientsActions.loadClientSubClients, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),

  on(ClientApiActions.loadPrimaryClientSuccess, (state, { client }) =>
    clientsAdapter.upsertOne(client, {
      ...state,
      // selectionIds: uniq(union(state.selectionIds, [client.id])),
      loaded: true,
    })
  )
);

export function clientsReducer(
  state: ClientsState | undefined,
  action: Action
) {
  return reducer(state, action);
}
